import { Component, Injector, OnInit } from '@angular/core';
import { AppConfigurationService } from '../../../app-configuration.service';
import { counter } from '../../../shared/constant/counter.constatnt';
import { Gender } from '../../../shared/enum/gender.enum';
import { Relationship } from '../../../shared/enum/relationship.enum';
import { YesNo } from '../../../shared/enum/yesNo.enum';
import { AdditionalInsurers } from '../../../shared/models/contract/additional-insurer.model';
import { PersonType } from '../../../shared/models/contract/person-type.model';
import { LookUpMasterModel } from '../../../shared/models/lookup-master/lookup-master.model';
import { CommonService } from '../../../shared/service/common.service';
import { StartUpConfigUiService } from '../../../shared/service/ui/start-up-config-ui.service';
import { BaseCJQuestion } from '../../extended-class/base-cj-question';
import { AdditionalInsurersValidationService } from '../../service/additional-insurer-validation.service';
import { Utils } from 'src/app/shared/Utils/Utils';

@Component({
  selector: 'app-insured-detail',
  templateUrl: './insured-detail.component.html'
})
export class InsuredDetailComponent extends BaseCJQuestion implements OnInit {
  public baseImageUrl: string;
  public showDobWarning = false;

  constructor(
    private commonService: CommonService,
    private insurersValidationService: AdditionalInsurersValidationService,
    private appConfigService: AppConfigurationService,
    serviceInjector: Injector,
    private startUpConfigUiService: StartUpConfigUiService
  ) {
    super(serviceInjector);
  }

  public ngOnInit(): void {
    if (this.customerJourneyService.contractType) {
      this.contractType = this.customerJourneyService.contractType;
    }

    if (this.appConfigService.getURLConfig?.baseImageUrl) {
      this.baseImageUrl = this.appConfigService.getURLConfig.baseImageUrl;
    }

    if (this.contractType?.policyHolder?.personType.length === 0) {
      this.contractType.policyHolder.personType.push(new PersonType());
    }

    this.counterService.processCounterByCounterName(counter.InsuredPage).subscribe();
    const sectionNumber = 1;
    const questionText = this.getAllSectionQuestionText(sectionNumber, true);
    this.startUpConfigUiService.sessionInfo$.subscribe({
      next: () => {
        this.startUpConfigUiService.loadConsentScript$.subscribe((isScriptLoaded) => {
          if (isScriptLoaded) {
            this.gtmDatalayerUiService.addVirtualPage('customerjourney', 'Q1', sectionNumber, questionText, 'default', 'V1').toPromise();
          }
        });
      }
    });
  }

  public moveNext(): void {
    if (this.validationUiService.isValidInsuredPage(this.contractType)) {
      this.addGTMAnswerString();
      this.customerJourneyService.updateContractType(this.contractType);
      this.router.navigate([Utils.getBaseRoute()], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        fragment: 'Q2'
      });
    }
  }

  public addInsurer(): void {
    if (this.contractType.policyHolder.personType.length < 9) {
      const insurer = new PersonType();
      this.contractType.policyHolder.personType.push(insurer);
      this.insurersValidationService.addInsurer();
    }
  }

  public removeInsurer(): void {
    if (this.contractType.policyHolder.personType.length > 1) {
      this.contractType.policyHolder.personType.pop();
      this.insurersValidationService.removeInsurer();
    }
  }

  public getFilteredQuestion(questionkey: string): LookUpMasterModel {
    return this.getQuestionModelByKey(questionkey);
  }

  public selectGender(value: number): void {
    this.contractType.policyHolder.personType[0].gender = value === 0 ? Gender.Hombre : Gender.Mujer;
  }

  public isGenderSelected(value: Gender): boolean {
    return this.contractType.policyHolder.personType[0].gender === value;
  }

  public submitPrimaryDOB(dob: string): void {
    const primaryInsured = this.contractType.policyHolder.personType[0];
    const normalizedDob = this.normalizeDobInput(dob);
    const formatDate = this.commonService.formatDob(normalizedDob);
    const backendFormat = this.toBackendDateTime(normalizedDob);
    if (formatDate === '') {
      primaryInsured.birthDate = new Date('invalid');
      primaryInsured.birthAgeInYears = 0;
      return;
    }

    primaryInsured.birthDate = backendFormat ? new Date(backendFormat) : this.commonService.formatDate(formatDate);
    primaryInsured.number = 1;
    primaryInsured.birthAgeInYears = this.commonService.getAgeUsingDateOfBirth(normalizedDob);
    primaryInsured.insured = YesNo.Yes;
  }

  public submitAdditionalDOB(insurer: AdditionalInsurers): void {
    const insuredPerson = this.contractType.policyHolder.personType[insurer.index + 1];
    const normalizedDob = this.normalizeDobInput(insurer.date);
    const formatDate = this.commonService.formatDob(normalizedDob);
    const backendFormat = this.toBackendDateTime(normalizedDob);
    if (formatDate === '') {
      insuredPerson.birthDate = new Date('invalid');
      insuredPerson.birthAgeInYears = 0;
      return;
    }

    insuredPerson.birthDate = backendFormat ? new Date(backendFormat) : this.commonService.formatDate(formatDate);
    insuredPerson.number = insurer.index + 2;
    insuredPerson.birthAgeInYears = this.commonService.getAgeUsingDateOfBirth(normalizedDob);
    insuredPerson.relationshipWithPolicyHolder = Relationship.Other;
    insuredPerson.gender = Gender.Hombre;
    insuredPerson.insured = YesNo.Yes;
  }

  public selectOtherHealthInsurance(value: number): void {
    this.contractType.otherHealthInsurance = value === 1;
  }

  public isOtherHealthInsurance(value: number): boolean {
    if (value === 1 && true === this.contractType.otherHealthInsurance) {
      return true;
    }
    if (value === 0 && false === this.contractType.otherHealthInsurance) {
      return true;
    }
    return false;
  }

  private normalizeDobInput(date: string): string {
    if (!date) {
      return '';
    }

    const normalized = date.trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalized)) {
      return normalized;
    }

    const parts = normalized.split('-');
    if (parts.length !== 3) {
      return normalized;
    }

    const [year, month, day] = parts;
    if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) {
      return normalized;
    }

    return `${day}/${month}/${year}`;
  }

  private toBackendDateTime(normalizedDob: string): string | null {
    const parts = normalizedDob.split('/');
    if (parts.length !== 3) {
      return null;
    }

    const [day, month, year] = parts;
    if (!/^\d{2}$/.test(day) || !/^\d{2}$/.test(month) || !/^\d{4}$/.test(year)) {
      return null;
    }

    return `${year}-${month}-${day}T01:00:00.000Z`;
  }

  private addGTMAnswerString(): void {
    const additionalInsured =
      this.contractType.policyHolder.personType.length > 0
        ? ' - ' +
          this.contractType.policyHolder.personType
            .filter((p) => p.number !== 1)
            ?.map((m) => m.birthAgeInYears)
            ?.join(' - ')
        : '';

    const gtmAnswerStr = `${this.contractType.otherHealthInsurance} - ${this.contractType.policyHolder.personType[0].birthAgeInYears} - ${this.contractType.policyHolder.personType[0].gender.toString()} ${additionalInsured}`;
    this.addGTMAnswer('Q1', gtmAnswerStr);
  }
}
